import { useMemo } from 'react';
import { useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

/**
 * A scrolling rail of SAMPLE signal events (Reuters/Bloomberg convention —
 * mono numbers, kind-tinted chips, JST timestamps). Honest by design: the
 * rail is labelled "SAMPLE SIGNALS / 直近のシグナル例" (these are illustrative,
 * not a live feed) and timestamps are computed at mount as `now − n minutes`
 * so the clock is never frozen. Marquee is pure CSS (zero JS); under
 * prefers-reduced-motion it collapses to a static 3-up row.
 */
function jstHHMM(minutesAgo: number): string {
  const d = new Date(Date.now() - minutesAgo * 60_000);
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Tokyo',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  } catch {
    return '--:--';
  }
}

export function SignalTicker() {
  const { t } = useTranslation();
  const reduced = useReducedMotion();

  // Tinted by kind — same palette as the radar so the eye learns the code.
  // Timestamps computed once at mount (minutes-ago offsets), never frozen.
  const items = useMemo(
    () => [
      { tint: '#10B981', kind: 'FUND', title: t('ticker.funding_techrise'), m: 4 },
      { tint: '#F59E0B', kind: 'JOB', title: t('ticker.jobchange_tanaka'), m: 9 },
      { tint: '#E11D48', kind: 'TDN', title: t('ticker.tdnet_yamato'), m: 24 },
      { tint: '#0A5BFF', kind: 'HIR', title: t('ticker.hiring_acme'), m: 38 },
      { tint: '#A855F7', kind: 'TCH', title: t('ticker.tech_meishi_kk'), m: 71 },
      { tint: '#10B981', kind: 'FUND', title: t('ticker.funding_kobalt'), m: 107 },
      { tint: '#F59E0B', kind: 'JOB', title: t('ticker.jobchange_sato'), m: 142 },
      { tint: '#0A5BFF', kind: 'EXEC', title: t('ticker.exec_hashimoto'), m: 178 },
    ],
    [t],
  );

  const Chip = ({ it }: { it: (typeof items)[number] }) => (
    <span className="inline-flex items-center gap-2 text-[12.5px]">
      <span
        className="num inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-[0.15em]"
        style={{ color: it.tint, backgroundColor: `${it.tint}1f` }}
      >
        {it.kind}
      </span>
      <span className="text-ink-100">{it.title}</span>
      <span className="num text-ink-400">{jstHHMM(it.m)} JST</span>
      <span className="text-ink-600">·</span>
    </span>
  );

  return (
    <div className="relative w-full overflow-hidden bg-ink-950 text-ink-50 border-y border-ink-800">
      <div className="flex items-stretch">
        {/* Left label rail — honest "SAMPLE", not a fake live feed. */}
        <div className="shrink-0 flex items-center gap-2 px-4 py-2.5 bg-accent-700 text-white text-[10px] uppercase tracking-[0.22em] font-semibold">
          {!reduced && (
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-300" />
            </span>
          )}
          {t('ticker.sample')}
        </div>

        {reduced ? (
          // Static, dense 3-up row — no marquee under reduced-motion.
          <div className="flex-1 min-w-0 flex items-center gap-8 overflow-hidden px-4 py-2.5">
            {items.slice(0, 3).map((it, i) => (
              <Chip key={i} it={it} />
            ))}
            <span className="num text-[10px] uppercase tracking-[0.18em] text-ink-500 ml-auto hidden md:inline">
              {t('ticker.reduced_note')}
            </span>
          </div>
        ) : (
          <div
            className="flex-1 min-w-0 overflow-hidden"
            style={{ '--marquee-duration': '70s' } as React.CSSProperties}
          >
            <div className="flex items-center gap-8 whitespace-nowrap animate-marquee py-2.5 will-change-transform">
              {[...items, ...items].map((it, i) => (
                <Chip key={i} it={it} />
              ))}
            </div>
          </div>
        )}

        <div className="shrink-0 hidden md:flex items-center px-4 py-2.5 text-[10px] uppercase tracking-[0.22em] text-ink-400">
          <span className="num">{t('ticker.last_24h')}</span>
        </div>
      </div>
    </div>
  );
}
